import { Inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { DOCUMENT } from '@angular/common';
import { filter, interval } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import * as Sentry from '@sentry/angular';

@Injectable({
    providedIn: 'root',
})
export class ServiceWorkerService {
    private deployingNewVersion = false;

    constructor(
        private swUpdate: SwUpdate,
        @Inject(DOCUMENT) private document: Document,
        private router: Router,
        private translateService: TranslateService,
        private messageService: MessageService
    ) {
        if (swUpdate.isEnabled) {
            const intervalTime = 1 * 60 * 1000;
            interval(intervalTime).subscribe(() =>
                swUpdate.checkForUpdate().catch((_error) => {
                    console.error('Error checking for updates:', _error);
                    Sentry.captureException(_error, {
                        tags: { component: 'ServiceWorker', action: 'checkForUpdate' },
                        level: 'warning',
                    });
                })
            );

            swUpdate.unrecoverable.subscribe((_event) => {
                const error = new Error(`Service worker unrecoverable state: ${_event.reason}`);
                console.error('Service worker unrecoverable state:', _event);
                Sentry.captureException(error, {
                    tags: {
                        component: 'ServiceWorker',
                        action: 'unrecoverable',
                        reason: String(_event.reason),
                    },
                    level: 'error',
                    extra: { reason: _event.reason, type: _event.type },
                });

                const summary = this.translateService.instant('service-worker.unrecoverable-title');
                const detail = this.translateService.instant('service-worker.unrecoverable-detail');

                this.messageService.add({
                    severity: 'error',
                    summary,
                    detail,
                    sticky: true,
                    key: 'unrecoverable-state',
                });

                setTimeout(() => {
                    this.refresh();
                }, 3000);
            });

            this.checkForUpdates();
        }
    }

    public checkForUpdates(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe((data) => {
                if (!this.deployingNewVersion) {
                    this.deployingNewVersion = true;
                    console.log('New version available:', data.latestVersion);

                    if (this.router.url.includes('/auth')) {
                        this.refresh();
                    } else {
                        const summary = this.translateService.instant('service-worker.update-available-title');
                        const detail = this.translateService.instant('service-worker.update-available-detail');

                        this.messageService.add({
                            severity: 'info',
                            summary,
                            detail,
                            sticky: true,
                            key: 'update-available',
                        });

                        window.dispatchEvent(
                            new CustomEvent('sw-update-available', {
                                detail: {
                                    version: data.latestVersion,
                                    message: detail,
                                },
                            })
                        );
                    }
                }
            });
    }

    async refresh() {
        try {
            await this.swUpdate.activateUpdate();
            this.deployingNewVersion = false;
            console.log('Activating update...');
            this.reloadWindow();
        } catch (_error) {
            console.error('Error activating update:', _error);
            Sentry.captureException(_error, {
                tags: { component: 'ServiceWorker', action: 'activateUpdate' },
                level: 'error',
            });
            this.deployingNewVersion = false;
            this.reloadWindow();
        }
    }

    private reloadWindow(): void {
        window.location.reload();
    }

    public manualCheckForUpdate(): Promise<boolean> {
        if (this.swUpdate.isEnabled) {
            return this.swUpdate.checkForUpdate();
        }
        return Promise.resolve(false);
    }
}
